import { AppointmentStatus } from '@prisma/client';
import { formatInTimeZone } from 'date-fns-tz';
import {
  AppointmentsSummaryCounts,
  ProfileAppointment,
} from '../../../../../types/customers';
import { getFullName } from '../../../../../helpers/global';
import { GetAllInPrice } from '../../../../../hooks/payments/useCashDiscount';
import {
  convertSnakeToTitleCase,
  formatDollarAmount,
} from '../../../../../utils/global/helper';

export type { ProfileAppointment, AppointmentsSummaryCounts };

export type AppointmentRowStatus =
  | 'upcoming'
  | 'completed'
  | 'no_show'
  | 'canceled';

export interface AppointmentRowContent {
  dateLabel: string;
  statusLine: string;
  statusVariant: 'muted' | 'danger';
  serviceLabel: string;
  artistLabel: string;
  valueLabel: string;
}

const STATUS_LABEL: Record<AppointmentRowStatus, string> = {
  upcoming: 'Upcoming',
  completed: 'Completed',
  no_show: 'No-show',
  canceled: 'Canceled',
};

export function getAppointmentDisplayStatus(
  appointment: Pick<
    ProfileAppointment,
    'status' | 'wasNoShow' | 'startDateTime'
  >,
  now: Date,
): AppointmentRowStatus {
  if (appointment.wasNoShow) return 'no_show';
  if (appointment.status === AppointmentStatus.CANCELED) return 'canceled';

  const startDateTime = appointment.startDateTime
    ? new Date(appointment.startDateTime)
    : null;

  if (!startDateTime || startDateTime >= now) return 'upcoming';
  return 'completed';
}

export function describeAppointmentRow(
  appointment: ProfileAppointment,
  timezone: string,
  now: Date,
  getAllInPrice: GetAllInPrice,
): AppointmentRowContent {
  const displayStatus = getAppointmentDisplayStatus(appointment, now);
  const startDateTime = appointment.startDateTime
    ? new Date(appointment.startDateTime)
    : null;

  const dateLabel = startDateTime
    ? formatInTimeZone(startDateTime, timezone, 'MMMM d, yyyy')
    : '—';
  const timeLabel = startDateTime
    ? formatInTimeZone(startDateTime, timezone, 'h:mm a')
    : null;

  const statusLine = [timeLabel, STATUS_LABEL[displayStatus]]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const serviceLabel =
    appointment.service?.serviceName ??
    convertSnakeToTitleCase(appointment.serviceCategory);

  const artistLabel = appointment.employees
    ? getFullName(appointment.employees.user)
    : '—';

  const valueLabel =
    displayStatus === 'canceled' || appointment.price === null
      ? '—'
      : formatDollarAmount(getAllInPrice(appointment.price));

  return {
    dateLabel,
    statusLine,
    statusVariant: displayStatus === 'no_show' ? 'danger' : 'muted',
    serviceLabel,
    artistLabel,
    valueLabel,
  };
}

export function formatAppointmentsSummaryLine({
  total,
  completed,
  noShow,
  canceled,
}: AppointmentsSummaryCounts): string {
  const appointmentWord = total === 1 ? 'appointment' : 'appointments';
  return `${total} ${appointmentWord} · ${completed} completed, ${noShow} no-shows, ${canceled} canceled`;
}
